import { useState } from 'react';
import GenericModal from 'components/app-components/modals/GenericModal';

type ModalView = 'select' | 'apple' | 'google';

interface AddToWalletModalProps {
  open: boolean;
  onClose: () => void;
}

const applePaySteps = [
  'Open the Wallet app on your iPhone',
  'Tap the "+" button in the top right corner',
  'Select "Debit or Credit Card"',
  'Tap "Continue" and position your card in the frame, or enter details manually',
  'Verify your card with your bank (SMS or email)',
  'Your card should be ready for contactless payments!',
];

const googlePaySteps = [
  'Open the Google Wallet app on your Android device',
  'Tap "Add to Wallet" then select "Payment card"',
  'Use your camera to scan the card or enter details manually',
  "Accept the issuer's terms and conditions",
  'Verify your card via SMS, email, or your banking app',
  'Your card should be ready for contactless payments!',
];

const TITLES: Record<ModalView, string> = {
  select: 'Add card to your preferred app',
  apple: 'Add to Apple Pay',
  google: 'Add to Google Pay',
};

const SUBTITLES: Record<ModalView, string> = {
  select: "Select the app you'd like to add your card to",
  apple: 'Follow these steps to add your card to Apple Pay on your iPhone',
  google: 'Follow these steps to add your card to Google Pay on your Android phone',
};

export const AddToWalletModal = ({ open, onClose }: AddToWalletModalProps) => {
  const [view, setView] = useState<ModalView>('select');

  const handleClose = () => {
    onClose();
    setTimeout(() => setView('select'), 200);
  };

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={TITLES[view]}
      subTitle={SUBTITLES[view]}
      titleCenter
      showHeaderBorder={false}
      width="md:min-w-md md:max-w-md w-full"
    >
      {view === 'select' && (
        <div className="flex flex-col gap-3">
          <button
            onClick={() => setView('apple')}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-3.5 transition-colors hover:bg-neutral-50"
          >
            <img src="/images/apple-wallet-logo.png" alt="apple-wallet" className="h-6 w-auto" />
            <span className="text-sm font-medium text-neutral-900">Apple Wallet</span>
          </button>
          <button
            onClick={() => setView('google')}
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-3.5 transition-colors hover:bg-neutral-50"
          >
            <img src="/images/Google_Pay_Logo.svg" alt="apple-wallet" className="h-6 w-auto" />
            <span className="text-sm font-medium text-neutral-900">Google Pay</span>
          </button>
        </div>
      )}

      {view === 'apple' && <StepsList steps={applePaySteps} onBack={() => setView('select')} />}
      {view === 'google' && <StepsList steps={googlePaySteps} onBack={() => setView('select')} />}
    </GenericModal>
  );
};

const StepsList = ({ steps, onBack }: { steps: string[]; onBack: () => void }) => {
  return (
    <div className="flex flex-col gap-1">
      {steps.map((step, index) => (
        <div key={index} className="flex items-start gap-4 rounded-md py-2.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-semibold text-neutral-600">
            {index + 1}
          </span>
          <p className="pt-0.5 text-sm text-neutral-700">{step}</p>
        </div>
      ))}
      <button
        onClick={onBack}
        className="mt-2 text-sm font-medium text-therablue transition-colors hover:text-therablue-dark"
      >
        Back to options
      </button>
    </div>
  );
};
